import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  Typography,
  Button,
  MenuItem,
  Menu,
  Select,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, X } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { useGetApi } from "../hooks/useGetApi";
import { getUserApi, getRoleApi, deleteUserByIdApi } from "../services/user.service";
import { getDepartmentApi } from "../services/department.service";
import { useSelector } from "react-redux";
import { AddCircleOutlineOutlined, MoreVert } from "@mui/icons-material";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { toast } from "react-toastify";
import { hasPermission } from "../utils/permissionHelper";

const headLabel = [
  {
    id: 1,
    label: "User ID",
    value: "userId",
  },
  {
    id: 2,
    label: "User Name",
    value: "userName",
  },
  {
    id: 3,
    label: "Unit",
    value: "unit",
  },
  {
    id: 4,
    label: "Department",
    value: "department",
  },
  {
    id: 5,
    label: "Role",
    value: "role",
  },
  {
    id: 6,
    label: "Email",
    value: "email",
  },
  {
    id: 7,
    label: "Contact Number",
    value: "contactNo",
  },
  {
    id: 8,
    label: "Status",
    value: "active",
  },
  {
    id: 9,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = ["Change Role", "Change Unit", "Edit User Details", "Delete"];

const User = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const currentUser = useSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedRow, setSelectedRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT });
  const [sortModel, setSortModel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNewUser = () => {
    navigate("add");
  };

  const handlePopoverClick = (option) => {
    if (option === "Delete") {
      handleDeleteClick();
    } else {
      handleClose(); // Close the menu first for navigation options
      if (option === popoverOptions[0]) {
        navigate(`${selectedRow?._id}/change-role`);
      } else if (option === popoverOptions[1]) {
        navigate(`${selectedRow?._id}/change-unit`);
      } else if (option === popoverOptions[2]) {
        navigate(selectedRow?._id);
      }
    }
  };

  const handleDeleteClick = () => {
    setItemToDelete(selectedRow);
    setDeleteDialogOpen(true);
    handleClose(); // Close the menu
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      const response = await deleteUserByIdApi(itemToDelete._id);
      if (response.code === 200) {
        toast.success("User deleted successfully!");
        refetch(); // Refresh the list
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDependencies(null);
      } else if (response?.code === 400 && response?.data) {
        // Handle dependency error
        setDependencies(response.data);
      } else {
        toast.error(response.message || "Failed to delete user");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        setDependencies(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the user");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDependencies(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDependencies(null);
  };

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handlePaginationModelChange = (newModel) => setPaginationModel(newModel);
  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);

  const {
    dataList: rawUserList,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { 
      limit: 10000, 
      page: 1, 
      search, 
      searchBy,
      role: selectedRole,
      dept: selectedDepartment
    },
    dependencies: [search, searchBy, selectedRole, selectedDepartment],
    debounceDelay: 500,
    dataKey: "users",
    countKey: "total",
  });

  // Filter user list based on current user's role
  const userList = React.useMemo(() => {
    if (!rawUserList) return [];
    
    // Super Admin can see all users
    if (currentUser.role === 'Super Admin') {
      return rawUserList;
    }
    
    // Admin can see all users except Super Admin users
    if (currentUser.role === 'Admin') {
      return rawUserList.filter(user => user.role?.role !== 'Super Admin');
    }
    
    // Other roles shouldn't see user list (this page should be blocked by navigation)
    return [];
  }, [rawUserList, currentUser.role]);

  // Fetch roles and departments for filters
  const {
    dataList: rawRoleList,
    isLoading: roleLoading,
  } = useGetApi({
    apiFunction: getRoleApi,
    params: {},
    dependencies: [],
    dataKey: "roles",
  });

  // Filter role list for the dropdown based on current user's role
  const roleList = React.useMemo(() => {
    if (!rawRoleList) return [];
    
    // Super Admin can see all roles in filter
    if (currentUser.role === 'Super Admin') {
      return rawRoleList;
    }
    
    // Admin can see all roles except Super Admin in filter
    if (currentUser.role === 'Admin') {
      return rawRoleList.filter(role => role.role !== 'Super Admin');
    }
    
    // Other roles shouldn't see this filter
    return [];
  }, [rawRoleList, currentUser.role]);

  const {
    dataList: departmentList,
    isLoading: departmentLoading,
  } = useGetApi({
    apiFunction: getDepartmentApi,
    params: {},
    dependencies: [],
    dataKey: "departments",
  });

  const rows = userList?.map((row) => ({
    id: row._id,
    _id: row._id,
    userId: row?.userId ?? "—",
    userName: `${row?.firstName || ""} ${row?.lastName || ""}`.trim(),
    unit: row?.unit?.unitName ?? "—",
    department: row?.department?.name ?? "—",
    role: row?.role?.role ?? "—",
    email: row?.email ?? "—",
    contactNo: row?.contactNo ?? "—",
    active: row?.active ? "Active" : "Inactive",
    firstName: row?.firstName,
    lastName: row?.lastName,
    unitObj: row?.unit,
    departmentObj: row?.department,
    roleObj: row?.role,
  })) ?? [];

  const getColumns = useCallback(() => {
    const baseColumns = [
      { field: "userId", headerName: "User ID", flex: isMobile ? 0 : 0.8, width: isMobile ? 100 : undefined, minWidth: 90, headerAlign: "center", align: "center", sortable: true },
      { field: "userName", headerName: "User Name", flex: isMobile ? 0 : 1.2, width: isMobile ? 120 : undefined, minWidth: 120, headerAlign: "center", align: "center", sortable: true },
      { field: "unit", headerName: "Unit", flex: isTablet ? 0 : 0.9, width: isTablet ? 100 : undefined, minWidth: 90, headerAlign: "center", align: "center", sortable: true },
      { field: "department", headerName: "Department", flex: isTablet ? 0 : 0.9, width: isTablet ? 110 : undefined, minWidth: 100, headerAlign: "center", align: "center", sortable: true },
      { field: "role", headerName: "Role", flex: isTablet ? 0 : 0.7, width: isTablet ? 90 : undefined, minWidth: 80, headerAlign: "center", align: "center", sortable: true },
      { field: "email", headerName: "Email", flex: isMobile ? 0 : 1.2, width: isMobile ? 140 : undefined, minWidth: 130, headerAlign: "center", align: "center", sortable: true },
      { field: "contactNo", headerName: "Contact Number", flex: isMobile ? 0 : 1, width: isMobile ? 110 : undefined, minWidth: 110, headerAlign: "center", align: "center", sortable: true },
      { field: "active", headerName: "Status", flex: isTablet ? 0 : 0.7, width: isTablet ? 80 : undefined, minWidth: 70, headerAlign: "center", align: "center", sortable: true },
      { field: "actions", headerName: "", flex: 0, width: 60, minWidth: 50, headerAlign: "center", align: "center", sortable: false, renderCell: (params) => (<IconButton size={isMobile ? "small" : "medium"} onClick={(e) => { e.stopPropagation(); handleClick(e, params.row); }} sx={{ cursor: "pointer" }}><MoreVert /></IconButton>) },
    ];
    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          py: "20px",
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              marginTop: "20px",
              marginBottom: "40px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "10px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "" ? "All Fields" : 
                      searchBy === "firstName" ? "First Name" : 
                      searchBy === "lastName" ? "Last Name" : 
                      searchBy === "email" ? "Email" : 
                      searchBy === "userId" ? "User ID" : 
                      searchBy === "contactNo" ? "Contact Number" : "All Fields").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="">All Fields</option>
                  <option value="firstName">First Name</option>
                  <option value="lastName">Last Name</option>
                  <option value="email">Email</option>
                  <option value="userId">User ID</option>
                  <option value="contactNo">Contact Number</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "" ? "All Fields" : 
                    searchBy === "firstName" ? "First Name" : 
                    searchBy === "lastName" ? "Last Name" : 
                    searchBy === "email" ? "Email" : 
                    searchBy === "userId" ? "User ID" : 
                    searchBy === "contactNo" ? "Contact Number" : "All Fields"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleNewUser}
            >
              <AddCircleOutlineOutlined />
              New User
            </Button>
          </Box>

          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "40px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  label="Role"
                  value={selectedRole || ""}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {roleList?.map((role) => (
                    <MenuItem key={role._id} value={role._id}>
                      {role.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="dept-select-label">Dept</InputLabel>
                <Select
                  labelId="dept-select-label"
                  label="Dept"
                  value={selectedDepartment || ""}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {departmentList?.map((dept) => (
                    <MenuItem key={dept._id} value={dept._id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* DataGrid Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <Box>
                <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", maxWidth: "100%", overflow: "hidden" }}>
                  <DataGrid
                    rows={rows}
                    columns={getColumns()}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    paginationMode="client"
                    sortingMode="client"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-root": { border: "none", width: "100%" },
                      "& .MuiDataGrid-main": { width: "100%" },
                      "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" },
                      "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
                      "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
                      "& .MuiDataGrid-virtualScroller": { minHeight: "200px" },
                      [theme.breakpoints.down("sm")]: { "& .MuiDataGrid-columnHeader": { fontSize: "11px", padding: "4px" }, "& .MuiDataGrid-cell": { fontSize: "11px", padding: "2px 4px" } },
                    }}
                    componentsProps={{ row: { onContextMenu: (event) => { event.preventDefault(); const rowId = event.currentTarget.getAttribute("data-id"); const rowData = rows.find((r) => String(r.id) === rowId); if (rowData) handleRightClick(event, rowData); } } }}
                    components={{ NoRowsOverlay: () => (<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No users found</Typography></Box>) }}
                  />
                </Box>
              </Box>

              {/* Pop-up Menu */}

              <Menu
                id={Boolean(anchorEl) ? "row-popover" : undefined}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={
                  anchorPosition
                    ? { top: anchorPosition.top, left: anchorPosition.left }
                    : undefined
                }
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {popoverOptions
                  ?.filter(option => {
                    if (option === "Delete") {
                      return hasPermission(currentUser, "Users.Delete");
                    }
                    return true;
                  })
                  ?.map((option, index) => (
                    <MenuItem 
                      key={index} 
                      onClick={() => handlePopoverClick(option)}
                    >
                      {option || ""}
                    </MenuItem>
                  ))}
              </Menu>

              {/* Delete Confirmation Dialog */}
              <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete User"
                itemName={itemToDelete ? `user "${itemToDelete.firstName} ${itemToDelete.lastName}"` : "this user"}
                dependencies={dependencies}
                isLoading={loading}
              />
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default User;
